import { useState, type ReactElement } from 'react';
import { formatDate, type DatasetLinkSummary, type LinkStatus } from '@tallyframe/core';
import {
  useDescribeSheet,
  useGoogleConnections,
  usePickerToken,
  usePullLink,
  useSaveLink,
  useUnlink
} from '../api/source-queries';
import { ApiError } from '../api/errors';
import type { LinkMismatch } from '../api/types';
import { useSession } from '../app/session-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { Banner } from '../ui/Banner';
import { Button } from '../ui/Button';
import { Stack } from '../ui/Stack';
import { ConnectGoogleButton, GoogleErrorBanner } from './connect';
import { pickSpreadsheet } from './picker';

/** The one action each derived status offers (spec, Data model). */
export const LINK_ACTION: Record<LinkStatus, string> = {
  active: 'Pull now',
  needs_reconnect: 'Reconnect Google',
  needs_relink: 'Re-link',
  unavailable: 'Re-pick the Sheet'
};

export interface LinkSectionProps {
  datasetId: string;
  reportId: string;
  link: DatasetLinkSummary | null;
  googleSheets: boolean;
  /** The Picker's public identifiers, from the app config. */
  picker?: { apiKey: string; appId: string };
  /** The callback's `?google_error=` code. */
  googleError?: string;
}

interface TabChoice {
  fileId: string;
  tabs: Array<{ sheetId: number; title: string }>;
}

function mismatchesOf(error: unknown): LinkMismatch[] {
  if (!ApiError.isApiError(error)) return [];
  const found = error.details.mismatches;
  return Array.isArray(found) ? (found as LinkMismatch[]) : [];
}

/** The data panel's link line and its one action; null when Google Sheets is off. */
export function LinkSection(props: LinkSectionProps): ReactElement | null {
  if (!props.googleSheets) return null;
  return <LinkBody {...props} />;
}

function LinkBody({ datasetId, reportId, link, picker, googleError }: LinkSectionProps): ReactElement {
  const returnTo = `/w/${useSession().snapshot.workspaceId ?? ''}/reports/${reportId}`;
  const connections = useGoogleConnections();
  const pull = usePullLink();
  const save = useSaveLink();
  const unlink = useUnlink();
  const describe = useDescribeSheet();
  const pickerToken = usePickerToken();

  const [choice, setChoice] = useState<TabChoice | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // The link prop only changes after the detail refetches; until then an unlinked one stays hidden.
  const [unlinked, setUnlinked] = useState<DatasetLinkSummary | null>(null);
  const shown = link !== null && link === unlinked ? null : link;

  const hasOwnConnection = connections.data?.connections.some((connection) => connection.mine) ?? false;
  const needsConnection = connections.data !== undefined && !hasOwnConnection;

  async function run(work: () => Promise<void>): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await work();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  const choose = () =>
    run(async () => {
      const { accessToken } = await pickerToken.mutateAsync();
      const fileId = await pickSpreadsheet({
        apiKey: picker?.apiKey ?? '',
        appId: picker?.appId ?? '',
        accessToken
      });
      if (fileId === null) return;
      const described = await describe.mutateAsync({ fileId });
      setChoice({ fileId, tabs: described.tabs });
    });

  const saveTab = (fileId: string, sheetId: number) =>
    run(async () => {
      await save.mutateAsync({ datasetId, ref: { fileId, sheetId } });
      setChoice(null);
    });

  const mismatches = mismatchesOf(error);

  let action: ReactElement | null = null;
  if (shown === null) {
    action = needsConnection ? (
      <ConnectGoogleButton returnTo={returnTo} />
    ) : (
      <Button size="sm" busy={busy} isDisabled={connections.data === undefined} onPress={() => void choose()}>
        Link a Google Sheet
      </Button>
    );
  } else if (shown.status === 'active') {
    action = (
      <Button
        size="sm"
        variant="primary"
        busy={busy}
        onPress={() => void run(async () => void (await pull.mutateAsync({ datasetId })))}
      >
        {LINK_ACTION.active}
      </Button>
    );
  } else if (shown.status === 'needs_reconnect') {
    action = <ConnectGoogleButton returnTo={returnTo} label={LINK_ACTION.needs_reconnect} />;
  } else if (shown.status === 'needs_relink') {
    action = (
      <Button
        size="sm"
        variant="primary"
        busy={busy}
        onPress={() => void run(async () => void (await save.mutateAsync({ datasetId, ref: null })))}
      >
        {LINK_ACTION.needs_relink}
      </Button>
    );
  } else {
    action = (
      <Button size="sm" variant="primary" busy={busy} onPress={() => void choose()}>
        {LINK_ACTION.unavailable}
      </Button>
    );
  }

  return (
    <Stack gap={2}>
      <GoogleErrorBanner code={googleError} />
      {shown ? (
        <p data-testid="link-line" className="m-0 p-0 font-ui text-sm text-ink">
          {`${shown.title} › ${shown.tabTitle}`}
          <span className="text-muted">
            {shown.lastPulledAt ? ` · Last pulled ${formatDate(shown.lastPulledAt)}` : ' · Never pulled'}
          </span>
        </p>
      ) : null}
      <div className="m-0 p-0 flex items-center gap-2">
        {action}
        {shown ? (
          <Button
            size="sm"
            variant="quiet"
            busy={busy}
            onPress={() =>
              void run(async () => {
                await unlink.mutateAsync({ datasetId });
                setUnlinked(shown);
              })
            }
          >
            Unlink
          </Button>
        ) : null}
      </div>
      {choice ? (
        <div className="m-0 p-0 flex flex-col gap-1">
          <p className="m-0 p-0 font-ui text-sm text-muted">Choose a tab</p>
          <div className="m-0 p-0 flex flex-wrap gap-2">
            {choice.tabs.map((tab) => (
              <Button key={tab.sheetId} size="sm" isDisabled={busy} onPress={() => void saveTab(choice.fileId, tab.sheetId)}>
                {tab.title}
              </Button>
            ))}
            <Button size="sm" variant="quiet" onPress={() => setChoice(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {mismatches.length > 0 ? (
        <Banner tone="danger" title="This tab does not fit the report">
          <ul className="m-0 pl-4">
            {mismatches.map((mismatch) => (
              <li key={`${mismatch.column}:${mismatch.problem}`}>{mismatch.message}</li>
            ))}
          </ul>
        </Banner>
      ) : error ? (
        <ErrorBanner error={error} />
      ) : null}
    </Stack>
  );
}
